// src/pages/Vehicles.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Stack,
  Button,
  TableContainer
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useVehicles from '../hooks/useVehicles';

// Highlight search matches in a subtle way
function highlightMatch(text, query) {
  if (!text) return '-';
  if (!query.trim()) return text;

  const str = String(text);
  const lowerStr = str.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const index = lowerStr.indexOf(lowerQuery);
  if (index === -1) return str;

  const before = str.slice(0, index);
  const match = str.slice(index, index + query.length);
  const after = str.slice(index + query.length);

  return (
    <>
      {before}
      <span
        style={{
          backgroundColor: 'rgba(255, 245, 157, 0.9)',
          borderRadius: 3,
          padding: '0 2px',
        }}
      >
        {match}
      </span>
      {after}
    </>
  );
}

// Optional: format date if we decide to show it later
function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function Vehicles() {
    const navigate = useNavigate()
  const { vehicles, loading } = useVehicles();

  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Filter vehicles by search
  const filteredVehicles = React.useMemo(() => {
    if (!search.trim()) return vehicles;

    const q = search.trim().toLowerCase();

    return vehicles.filter((v) => {
      const vrm = v.vrm || '';
      const makeModel = v.make_model || v.make || '';
      const licence = v?.phv?.licence_number || '';
      const colour = v.color || '';

      return (
        vrm.toLowerCase().includes(q) ||
        makeModel.toLowerCase().includes(q) ||
        licence.toLowerCase().includes(q) ||
        colour.toLowerCase().includes(q)
      );
    });
  }, [vehicles, search]);

  // When search changes, reset to first page
  React.useEffect(() => {
    setPage(0);
  }, [search]);

  // Pagination slice
  const paginatedVehicles = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredVehicles.slice(start, start + rowsPerPage);
  }, [filteredVehicles, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Vehicles
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        View and search your fleet. Dates and tests are managed via the calendar.
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            p: 2,
          }}
        >
          {/* Header row: search + counts */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 2,
            }}
          >
            <TextField
              label="Search vehicles…"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ maxWidth: 320 }}
              placeholder="Search by VRM, licence, make, colour…"
            />

            <Box sx={{ flexGrow: 1 }} />

            <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                onClick={() => navigate('/vehicles/new')}
            >
                Add Vehicle
            </Button>
          </Box>

          {/* Table */}
          <TableContainer sx={{overflowX: 'scroll'}}>
            <Table size="small" sx={{overflow: 'scrollX'}}>
                <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>VRM</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Licence Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Make &amp; Model</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fuel &amp; Transmission</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {paginatedVehicles.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={5}>
                        <Typography variant="body2" sx={{ py: 2 }}>
                        No vehicles match this search.
                        </Typography>
                    </TableCell>
                    </TableRow>
                )}

                {paginatedVehicles.map((v) => {
                    const vrm = v.vrm || '-';
                    const licence = v?.phv?.licence_number || '-';
                    const makeModel = v.make_model || v.make || '-';
                    const colour = v.color || '-';
                    const bodyType = v.body_type || '-';
                    const seats =
                    typeof v.seating_capacity === 'number'
                        ? v.seating_capacity
                        : v.seating_capacity || '-';
                    const fuel = v.fuel_type || '-';
                    const transmission = v.transmission_type || '-';
                    const firstReg = formatDate(v.date_first_registered);

                    return (
                    <TableRow
                        key={v.vrm}
                        hover
                        sx={{
                        '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(249,250,251,0.9)',
                        },
                        }}
                    >
                        {/* VRM */}
                        <TableCell>
                        <Typography
                            sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            color: "primary.main",
                            textDecoration: "underline",
                            }}
                            onClick={() => navigate(`/vehicles/${v._id}`)}
                        >
                            {v.vrm}
                        </Typography>
                        </TableCell>

                        {/* Licence number */}
                        <TableCell>
                        {licence !== '-' ? (
                            <Chip
                            size="small"
                            label={highlightMatch(licence, search)}
                            sx={{ fontWeight: 500 }}
                            color="primary"
                            variant="outlined"
                            />
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            -
                            </Typography>
                        )}
                        </TableCell>

                        {/* Make & model (plus optional first reg date) */}
                        <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {highlightMatch(makeModel, search)}
                        </Typography>
                        {firstReg && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            First registered: {firstReg}
                            </Typography>
                        )}
                        </TableCell>

                        {/* Details: colour, body, seats */}
                        <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                            size="small"
                            label={highlightMatch(colour, search)}
                            variant="outlined"
                            />
                            <Chip
                            size="small"
                            label={bodyType}
                            variant="outlined"
                            />
                            <Chip
                            size="small"
                            label={`${seats} seats`}
                            variant="outlined"
                            />
                        </Stack>
                        </TableCell>

                        {/* Fuel & Transmission */}
                        <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                            size="small"
                            label={fuel}
                            color="secondary"
                            variant="outlined"
                            />
                            <Chip
                            size="small"
                            label={transmission}
                            variant="outlined"
                            />
                        </Stack>
                        </TableCell>
                    </TableRow>
                    );
                })}
                </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredVehicles.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
